import {defineStore} from 'pinia'

export const useUserStore = defineStore('user-store', {
    state: () => ({
        profile: null,
        httpToken: null,
        wsToken: null,
        accessibleBusinesses: [],
        activeBusinessId: null,
        activeBusinessName: null,
        posPrinterName: null,
        notificationSound: '/sounds/alert.mp3',
        notificationSoundEnabled: true,
        notificationSoundLoop: false,
    }),
    getters: {
        isAuthenticated: state => !!state.profile,
        isCustomer: state => !!state.httpToken && !!state.profile && state.profile.role === 'Customer',
        isCompany: state => !!state.httpToken && !!state.profile && state.profile.role === 'Company',
        userInitials: state => {
            if (state.profile && state.profile.firstName) {
                return (state.profile.firstName[0] || '').toUpperCase()
            }

            return 'G'
        },
        getWsToken: state => state.profile?.wsToken,

        isWsTokenExpired: state => {
            if (!state.wsToken) return true

            try {
                const payloadBase64 = state.wsToken.split('.')[1]
                const decodedPayload = JSON.parse(atob(payloadBase64))
                const expiryTimestamp = decodedPayload.exp * 1000
                const now = Date.now()
                const buffer = 60 * 1000
                return now >= (expiryTimestamp - buffer)
            } catch (e) {
                console.error('[User Store] Failed to decode wsToken:', e)
                return true
            }
        },

        activeBusiness: state => {
            if (!state.activeBusinessId || state.accessibleBusinesses.length === 0) {
                return null
            }

            return state.accessibleBusinesses.find(b => b.id === state.activeBusinessId)
        },
    },
    actions: {
        async login(userProfile, accessTokenPayload) {
            this.profile = userProfile
            this.httpToken = accessTokenPayload
            console.log("User logged in, profile set in store:", this.profile)

            // Show customer display window if it exists (Tauri desktop app)
            try {
                const { invoke } = await import('@tauri-apps/api/core')
                const result = await invoke('show_customer_display')
                console.log(`[User Store] Customer display: ${result}`)
            } catch (error) {
                console.log("[User Store] Customer display not available (not in Tauri or error):", error)
            }
        },
        async logout() {
            console.log("User logging out from store.")

            // Hide customer display window if it exists (Tauri desktop app)
            try {
                const { invoke } = await import('@tauri-apps/api/core')
                const result = await invoke('hide_customer_display')
                console.log(`[User Store] Customer display hidden: ${result}`)
            } catch (error) {
                console.log("[User Store] Customer display not available (not in Tauri or error):", error)
            }

            // Tear down cached payment-provider instances. Without this, the
            // next user's session inherits the previous user's pairing state
            // and credentials. clearAllProviderInstances calls .disconnect()
            // on each cached provider before evicting from the module Map.
            try {
                const { clearAllProviderInstances } = await import('~/composables/usePaymentProvider')
                clearAllProviderInstances()
            } catch (e) {
                console.warn("[User Store] Failed to clear payment providers on logout:", e)
            }

            // Only clear auth-related data, keep device settings
            this.profile = null
            this.httpToken = null
            this.wsToken = null
            this.accessibleBusinesses = []
            this.activeBusinessId = null
            this.activeBusinessName = null

            // Preserve: posPrinterName, notificationSound, notificationSoundEnabled, notificationSoundLoop

            navigateTo('/login', {replace: true})
        },
        setProfile(userProfile) {
            this.profile = userProfile
        },
        setHttpToken(tokenPayload) {
            this.httpToken = tokenPayload
        },
        setWsToken(token) {
            this.wsToken = token
        },
        setInitialSetupData(setupData) {
            this.wsToken = setupData.wsToken
            this.accessibleBusinesses = setupData.accessibleBusinesses || []
        },
        setActiveBusiness(businessId) {
            const business = this.accessibleBusinesses.find(b => b.id === businessId)
            if (business) {
                this.activeBusinessId = business.id
                this.activeBusinessName = business.bName
                console.log(`Set active business: ${business.bName} (ID: ${business.id})`)
            } else {
                console.error(`Could not find business with ID: ${businessId}`)
            }
        },
        setPosPrinter(printerName) {
            this.posPrinterName = printerName
            console.log(`POS printer for session set to: "${printerName}"`)
        },
        setNotificationSound(soundUrl) {
            this.notificationSound = soundUrl
            console.log(`Notification sound set to: "${soundUrl}"`)
        },
        setNotificationSoundEnabled(enabled) {
            this.notificationSoundEnabled = enabled
            console.log(`Notification sound enabled: ${enabled}`)
        },
        setNotificationSoundLoop(loop) {
            this.notificationSoundLoop = loop
            console.log(`Notification sound loop: ${loop}`)
        },
    },
    persist: {
        key: 'user-store',

        // In Tauri, localStorage is scoped to the webview and persists.
        storage: localStorage,

        paths: [
            'profile',
            'httpToken',
            'wsToken',
            'accessibleBusinesses',
            'activeBusinessId',
            'activeBusinessName',
            'posPrinterName',
            'notificationSound',
            'notificationSoundEnabled',
            'notificationSoundLoop',
        ],
    },
})
